/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import throttleImg from 'assets/images/throttle.png'
import debounceImg from 'assets/images/debounce.png'

export const _2 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>2 - Throttle и Debounce</h2>
    <p className={styles.description}>
      Довольно часто необходимо реализовать какой-то функционал при изменении размера окна, скроллинге или вводе
      пользователя. Это может быть сортировка коллекции и отрисовка результатов, анимация, манипуляции с DOM-деревом и
      прочее. Все это улучшает UX (user experience), но к сожалению, дают большую нагрузку на браузер, из-за того что
      обработчики событий срабатывают слишком часто.
    </p>
    <p className={styles.description}>
      Например, если прикрепить обработчик скрола к элементу и прокрутить этот элемент с помощью трекпада, колеса
      прокрутки или просто перетаскиванием полосы прокрутки, можно вызвать около 30 событий в секунду. Но медленная
      прокрутка (свайп) в смартфоне может вызвать до 100 событий в секунду. Если обработчик события выполняет тяжелые
      вычисления и другие манипуляции с DOM, гарантированно будут проблемы с производительностью.
    </p>
    <p className={styles.description}>
      <b>Throttle</b> и <b>Debounce</b> - это два похожих (но разных по поведению) приема, позволяющих контролировать,
      сколько раз мы разрешаем выполнение функции с течением времени. Для ознакомления с этими методами будем
      использовать их реализацию из библиотеки{' '}
      <a className={styles.link} rel="noopener noreferrer" href="https://lodash.com/" target="_blank">
        Lodash
      </a>
      .
    </p>
    <p className={styles.notificationInfo}>
      Используя эти два подхода мы не контролируем как часто браузер будет генерировать события. Мы всего-лишь берем
      контроль над частотой выполнения callback-функции переданной в обработчик события.
    </p>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.1 - Throttle</h3>
      <p className={styles.description}>
        Прием <code className={styles.code}>throttle</code> обеспечивает контроль над количеством раз, которое функция
        может быть вызвана в течение промежутка времени. То есть позволяет выполнять функцию не чаще чем один раз в{' '}
        <code className={styles.code}>N</code> миллисекунд, гарантируя ее регулярное выполнение.
      </p>
      <img className={styles.img} src={throttleImg} alt="throttle" />
      <CopyBlock
        text={`window.addEventListener(
  'scroll',
  _.throttle(() => {
    console.log('Scroll event handler invocation every 300ms.');
  }, 300),
);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          rel="noopener noreferrer"
          href="https://lodash.com/docs/4.17.15#throttle"
          target="_blank"
        >
          Ссылка на документацию _.throttle()
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.2 - Debounce</h3>
      <p className={styles.description}>
        Прием <code className={styles.code}>debounce</code> гарантирует, что функция не будет вызывана снова, пока не
        пройдет определенное количество времени без ее вызова. То есть позволяет выполнить функцию, только если прошло{' '}
        <code className={styles.code}>N</code> миллисекунд без ее вызова, группируя множественные вызовы в один.
      </p>
      <img className={styles.img} src={debounceImg} alt="debounce" />
      <CopyBlock
        text={`document.querySelector('input').addEventListener(
  'input',
  _.debounce(() => {
    console.log(
      'Input event handler invocation after 300ms of inactivity past burst.',
    );
  }, 300),
);`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        <a
          className={styles.link}
          rel="noopener noreferrer"
          href="https://lodash.com/docs/4.17.15#debounce"
          target="_blank"
        >
          Ссылка на документацию _.debounce()
        </a>
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>2.3 - Дополнительные материалы</h3>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://webdevkin.ru/posts/frontend/kak-ispolzovat-throttle-i-debounce"
            target="_blank"
            rel="noopener noreferrer"
          >
            Как и зачем использовать throttle и debounce
          </a>
        </li>
        <li className={styles.list__item}>
          <a
            className={styles.link}
            href="https://css-tricks.com/debouncing-throttling-explained-examples/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Debouncing and Throttling Explained Through Examples
          </a>
        </li>
      </ul>
    </section>
  </section>
))
